/**
 * dm-api-serverless wrappers. Every call requires HMAC + Bearer.
 *
 * Endpoints:
 *   - POST /api/v0/dm/conversations                       create or fetch DM
 *   - GET  /api/v0/dm/conversations                       list with pagination
 *   - GET  /api/v0/dm/conversations/{id}                  single conversation + peer
 *   - GET  /api/v0/dm/unread-summary                      total unread for header badge
 *   - GET  /api/v0/dm/conversations/{id}/messages         message page
 *   - POST /api/v0/dm/conversations/{id}/messages         send a message
 *   - POST /api/v0/dm/conversations/{id}/read             mark read
 */

import { dmRequest, DM_ENV } from './dmApiClient';

function peerFromPairKey(pairKey, callerId) {
  if (!pairKey) return null;
  const [a, b] = String(pairKey)
    .split(':')
    .map((s) => Number(s));
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  if (a === callerId) return b;
  if (b === callerId) return a;
  return null;
}

function toPeerAvatar(raw, peerUserId) {
  return {
    user_id: peerUserId ?? null,
    object_key: raw?.objectKey || null,
    profile_photo: raw?.profilePhoto || null,
    s3_endpoint: raw?.s3Endpoint || null,
  };
}

function normalizeLatestMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (!raw.messageId || !raw.messageType || !raw.createdAt) return null;
  return {
    messageId: String(raw.messageId),
    messageType: String(raw.messageType),
    messageText: typeof raw.messageText === 'string' ? raw.messageText : null,
    createdAt: String(raw.createdAt),
  };
}

export async function createConversation(targetUserId, accessToken) {
  const res = await dmRequest(DM_ENV.dmApiBase, '/api/v0/dm/conversations', {
    method: 'POST',
    body: { targetUserId },
    accessToken,
  });
  return res?.data;
}

export async function listConversations(
  { limit = 30, beforeUpdatedAt, includeArchived = false, callerUserId } = {},
  accessToken
) {
  const res = await dmRequest(DM_ENV.dmApiBase, '/api/v0/dm/conversations', {
    method: 'GET',
    query: {
      limit,
      beforeUpdatedAt,
      includeArchived: includeArchived ? 'true' : undefined,
    },
    accessToken,
  });
  const d = res?.data || {};
  const conversations = Array.isArray(d.conversations) ? d.conversations : [];
  return {
    conversations: conversations.map((c) => {
      const peerUserId =
        typeof c?.peerUserId === 'number'
          ? c.peerUserId
          : peerFromPairKey(c?.pairKey, Number(callerUserId));
      return {
        ...c,
        latestMessage: normalizeLatestMessage(c?.latestMessage),
        peerUserId,
        peerAvatar: toPeerAvatar(c, peerUserId),
      };
    }),
    nextBeforeCursor: d.nextBeforeCursor || null,
  };
}

export async function getConversation(conversationId, accessToken) {
  const res = await dmRequest(
    DM_ENV.dmApiBase,
    `/api/v0/dm/conversations/${conversationId}`,
    { method: 'GET', accessToken }
  );
  const data = res?.data || {};
  const peerUserId = typeof data?.peerUserId === 'number' ? data.peerUserId : null;
  return {
    ...data,
    peerUserId,
    peerAvatar: toPeerAvatar(data, peerUserId),
  };
}

export async function getUnreadSummary(accessToken) {
  const res = await dmRequest(DM_ENV.dmApiBase, '/api/v0/dm/unread-summary', {
    method: 'GET',
    accessToken,
  });
  const d = res?.data || {};
  return {
    totalUnread: Number(d.totalUnread || 0),
    conversationsWithUnread: Number(d.conversationsWithUnread || 0),
  };
}

export async function getMessages(
  conversationId,
  { limit = 50, afterSeq, beforeSeq } = {},
  accessToken
) {
  const res = await dmRequest(
    DM_ENV.dmApiBase,
    `/api/v0/dm/conversations/${conversationId}/messages`,
    {
      method: 'GET',
      query: { limit, afterSeq, beforeSeq },
      accessToken,
    }
  );
  const d = res?.data || {};
  return {
    messages: Array.isArray(d.messages) ? d.messages : [],
    nextCursor: typeof d.nextCursor === 'number' ? d.nextCursor : null,
  };
}

export async function postMessage(
  conversationId,
  { body, mediaUrl, messageType = 'TEXT', clientMessageId },
  accessToken
) {
  const payload = {
    messageType,
    body,
    clientMessageId,
  };
  if (messageType === 'IMAGE') payload.mediaUrl = mediaUrl;
  const res = await dmRequest(
    DM_ENV.dmApiBase,
    `/api/v0/dm/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      body: payload,
      accessToken,
    }
  );
  return res?.data;
}

export async function markRead(conversationId, lastReadSeq, accessToken) {
  const res = await dmRequest(
    DM_ENV.dmApiBase,
    `/api/v0/dm/conversations/${conversationId}/read`,
    {
      method: 'POST',
      body: { lastReadSeq },
      accessToken,
    }
  );
  return res?.data;
}
